import { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react'

const SettingsContext = createContext(null)

export const useSettingsContext = () => useContext(SettingsContext)

// Normalize a value for comparison: treat null, undefined, '' as equivalent
const normalize = (val) => {
    if (val === null || val === undefined || val === '') return null
    if (Array.isArray(val)) return val.map(normalize)
    if (typeof val === 'object') {
        const out = {}
        for (const [k, v] of Object.entries(val)) out[k] = normalize(v)
        return out
    }
    return val
}

// Stable stringification for snapshot comparison
export const settingsSnapshot = (obj) => JSON.stringify(normalize(obj))

export const SettingsProvider = ({ children }) => {
    const [hasChanges, setHasChanges] = useState(false)
    const [isSaving, setIsSaving]     = useState(false)
    const saveRef  = useRef(null)
    const cancelRef = useRef(null)
    const activeIdRef = useRef(null)

    // Each tab calls registerTab to wire up its save/cancel handlers
    const registerTab = useCallback(({ onSave, onCancel }) => {
        const id = {}
        activeIdRef.current = id
        saveRef.current  = onSave
        cancelRef.current = onCancel
        return id
    }, [])

    const unregisterTab = useCallback((id) => {
        // Only clear if this tab is still the active registration
        if (id && activeIdRef.current !== id) return
        activeIdRef.current = null
        saveRef.current  = null
        cancelRef.current = null
        setHasChanges(false)
        setIsSaving(false)
    }, [])

    const triggerSave = useCallback(async () => {
        if (typeof saveRef.current !== 'function') return
        setIsSaving(true)
        try {
            await saveRef.current()
        } catch {
            // Validation rejections are handled inside each tab's submit
        } finally {
            setIsSaving(false)
        }
    }, [])

    const triggerCancel = useCallback(() => {
        if (cancelRef.current) cancelRef.current()
        setHasChanges(false)
    }, [])

    const value = useMemo(() => ({
        hasChanges, setHasChanges,
        isSaving,
        registerTab, unregisterTab,
        triggerSave, triggerCancel,
    }), [hasChanges, isSaving, registerTab, unregisterTab, triggerSave, triggerCancel])

    return (
        <SettingsContext.Provider value={value}>
            {children}
        </SettingsContext.Provider>
    )
}
